import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    ArrowDownUp,
    CheckSquare,
    CornerUpRight,
    FileStack,
    LockKeyhole,
    LockKeyholeOpen,
    MinusCircle,
    SquarePen,
    Trash2,
    XIcon,
} from "lucide-react";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { usePermissions } from "../../hooks/usePermissions";
import type { ActionBarProps } from ".";
import type { Event } from "../../types/Event";

export const useConfig = ({
    openActionsModal,
    openCopyModal,
    openTransferModal,
    openDeleteModal,
}: ActionBarProps) => {
    const { t } = useTranslation();
    const {
        type,
        selectedEvents,
        selectedUnassignedEvents,
        events,
        calendarEvents,
        unassignedEvents,
        dispatch,
        clearSelectedEvents
    } = useScheduleContext();

    const [filters] = useFilters();
    const {
        canCopy,
        canEdit: _canEdit,
        canDelete: _canDelete,
        canApprove,
        canLock,
        canTransfer,
        canUnlock,
        canEditUnassignedShift,
        canDeleteUnassignedShift
    } = usePermissions();
    const canEdit = selectedUnassignedEvents?.length > 0 ? canEditUnassignedShift : _canEdit;
    const canDelete = selectedUnassignedEvents?.length > 0 ? canDeleteUnassignedShift : _canDelete;

    const allMonthEvents = useMemo(() => {
        if (filters?.mode !== "month" || !calendarEvents) return [];
        return Object.values(calendarEvents).flatMap(({ events }: { events: Event[] }) => events || []);
    }, [calendarEvents, filters?.mode]);

    const disabledSelectAll = useMemo(() => {
        const isMonth = filters?.mode === "month";
        const hasUnassigned = selectedUnassignedEvents?.length > 0 && isMonth;

        if (hasUnassigned) {
            return selectedUnassignedEvents?.length === unassignedEvents?.length;
        } else {
            const totalInView = isMonth ? allMonthEvents.length : events?.length;
            const currentSelected = selectedEvents?.length;
            return currentSelected === totalInView && totalInView > 0;
        }
    }, [filters?.mode, selectedEvents, selectedUnassignedEvents, unassignedEvents, events, allMonthEvents]);

    const list = useMemo(() => {
        const isMonth = filters?.mode === "month";

        if (isMonth && selectedUnassignedEvents?.length > 0) {
            return selectedUnassignedEvents;
        }

        if (selectedEvents?.length > 0) {
            return selectedEvents.filter((item: Event) => item?.type !== "unavailable");
        }

        const data = isMonth ? allMonthEvents : events;
        return data?.filter((event: Event) => event?.type !== "unavailable") || [];
    }, [filters?.mode, selectedEvents, selectedUnassignedEvents, events, allMonthEvents]);

    const canApproveEvent = useMemo(() => {
        const hasPending = list?.some((event) => event.status === "pending" && !event?.locked);
        if (filters?.mode === "month" && selectedUnassignedEvents?.length > 0) return false;
        return canApprove && hasPending;
    }, [canApprove, filters?.mode, selectedUnassignedEvents, list]);

    const canUnapproveEvent = useMemo(() => {
        const hasApproved = list?.some((event) => event.status === "approved" && !event?.locked);
        if (filters?.mode === "month" && selectedUnassignedEvents?.length > 0) return false;
        return canApprove && hasApproved;
    }, [list, filters?.mode, selectedUnassignedEvents, canApprove]);

    const canLockEvent = useMemo(() => {
        const hasUnlockedEvents = list?.some((event) => !event?.locked);
        if (filters?.mode === "month" && selectedUnassignedEvents?.length > 0) return false;
        return canLock && hasUnlockedEvents;
    }, [list, filters?.mode, selectedUnassignedEvents, canLock]);

    const canUnlockEvent = useMemo(() => {
        const hasLockedEvents = list?.some((event) => event?.locked);
        if (filters?.mode === "month" && selectedUnassignedEvents?.length > 0) return false;
        return canUnlock && hasLockedEvents;
    }, [list, filters?.mode, selectedUnassignedEvents, canUnlock]);

    const hasDeletableEvents = useMemo(
        () => !!list?.length && _canDelete && !list.every((event) => event?.locked),
        [list, _canDelete]
    );

    // in month mode nothing selected means "delete everything in view", and the view owns the unassigned shifts too
    const unassignedIds = useMemo(() => {
        const hasSelection = selectedEvents?.length > 0 || selectedUnassignedEvents?.length > 0;
        if (filters?.mode !== "month" || hasSelection || !canDeleteUnassignedShift) return [];
        return (unassignedEvents || []).map(({ id }: { id: string }) => id);
    }, [filters?.mode, selectedEvents, selectedUnassignedEvents, canDeleteUnassignedShift, unassignedEvents]);

    const canDeleteEvent = useMemo(() => {
        if (filters?.mode === "month" && selectedUnassignedEvents?.length > 0) {
            return canDelete;
        }
        if (unassignedIds.length > 0) return true;
        if (!list?.length) return false;
        return canDelete && !list.every((event) => event?.locked);
    }, [filters?.mode, selectedUnassignedEvents, canDelete, list, unassignedIds]);

    const onSelectAll = useCallback(() => {
        const isMonth = filters?.mode === "month";
        const hasUnassigned = selectedUnassignedEvents?.length > 0 && isMonth;

        const actionType = hasUnassigned ? "select-unassigned-events" : "select-events";
        const payload = hasUnassigned ? unassignedEvents : (isMonth ? allMonthEvents : events);
        dispatch({ type: actionType, payload: payload?.filter((item) => item?.type !== "unavailable") });
    }, [dispatch, events, allMonthEvents, filters?.mode, unassignedEvents, selectedUnassignedEvents]);

    const close = useCallback(() => {
        dispatch({ type: "ctrl-shift-s" });
        clearSelectedEvents();
    }, [dispatch, clearSelectedEvents]);

    const submit = useCallback((action: string) => {
        const ids = selectedEvents?.map(({ id }: { id: string }) => id);
        openActionsModal({
            ids,
            action,
            type: "bulk"
        });
    }, [openActionsModal, selectedEvents]);

    const isTemplate = useMemo(() => {
        return ["template", "profile", "my-profile"].includes(type);
    }, [type]);

    return useMemo(() => [
        {
            id: "select-all",
            label: t("select-all"),
            icon: CheckSquare,
            hidden: false,
            disabled: disabledSelectAll,
            onChange: onSelectAll
        },
        {
            id: "edit",
            label: t("edit"),
            icon: SquarePen,
            hidden: false,
            disabled: !canEdit ||
                selectedEvents?.[0]?.locked ||
                (filters?.mode === "month"
                    ? (selectedEvents?.length + selectedUnassignedEvents?.length !== 1)
                    : selectedEvents?.length !== 1),
            onChange: () => {
                const selected = filters?.mode === "month" ? (selectedEvents?.[0] || selectedUnassignedEvents?.[0]) : selectedEvents?.[0];
                const isUnassignedShift = filters?.mode === "month" && selectedUnassignedEvents?.length > 0;

                dispatch({
                    type: "selected-cell-or-event",
                    payload: {
                        ...selected,
                        isCreating: false,
                        isUnassignedShift,
                        isTemplate
                    }
                });
            },
        },
        {
            id: "copy",
            label: t("copy"),
            icon: FileStack,
            hidden: false,
            disabled: !canCopy ||
                (selectedEvents?.length > 0 && !isTemplate) ||
                (selectedEvents?.length !== 1 && isTemplate) ||
                (selectedUnassignedEvents?.length > 0 && filters?.mode === "month"),
            onChange: () => {
                const ids = selectedEvents?.map(({ id }: { id: string }) => id);
                const data = isTemplate ? {
                    event: selectedEvents[0].id,
                    type: "cell",
                    isTemplate
                } : ids;
                openCopyModal(data);
            },
        },
        {
            id: "transfer",
            label: t("transfer"),
            icon: ArrowDownUp,
            hidden: false,
            disabled: !canTransfer || selectedEvents?.length !== 1 || selectedEvents?.[0]?.locked || selectedUnassignedEvents?.length > 0,
            onChange: () => {
                const selected = selectedEvents?.[0];
                openTransferModal({
                    type: "cell",
                    eventId: selected?.id,
                    userId: selected?.user,
                    date: selected?.date,
                    isTemplate
                });
            },
        },
        {
            id: "lock",
            label: t("lock"),
            icon: LockKeyhole,
            hidden: isTemplate,
            disabled: !canLockEvent,
            onChange: () => submit("lock"),
        },
        {
            id: "unlock",
            label: t("unlock"),
            icon: LockKeyholeOpen,
            hidden: isTemplate,
            disabled: !canUnlockEvent,
            onChange: () => submit("unlock"),
        },
        {
            id: "publish",
            label: t("publish"),
            icon: CornerUpRight,
            hidden: isTemplate,
            disabled: !canApproveEvent,
            onChange: () => submit("publish"),
        },
        {
            id: "unpublish",
            label: t("unpublish"),
            icon: MinusCircle,
            hidden: isTemplate,
            disabled: !canUnapproveEvent,
            onChange: () => submit("unpublish"),
        },
        {
            id: "delete",
            label: t("delete"),
            icon: Trash2,
            hidden: false,
            disabled: !canDeleteEvent,
            onChange: () => {
                const isUnassignedSelection = filters?.mode === "month" && selectedUnassignedEvents?.length > 0;
                const ids = isUnassignedSelection
                    ? selectedUnassignedEvents.map((e) => e.id)
                    : selectedEvents?.map(({ id }: { id: string }) => id);

                openDeleteModal({
                    isTemplate,
                    ids: ids as string[],
                    type: isUnassignedSelection ? "bulk-unassign" : "bulk",
                    ...(unassignedIds.length > 0 && {
                        unassignedIds,
                        hasEvents: hasDeletableEvents
                    })
                });
            },
        },
        {
            id: "close",
            label: t("close"),
            icon: XIcon,
            onChange: close,
        }
    ], [
        isTemplate,
        disabledSelectAll,
        canEdit,
        selectedEvents,
        filters?.mode,
        selectedUnassignedEvents,
        canCopy,
        canTransfer,
        canLockEvent,
        canUnlockEvent,
        canApproveEvent,
        canUnapproveEvent,
        canDeleteEvent,
        hasDeletableEvents,
        unassignedIds,
        submit,
        openDeleteModal,
        openTransferModal,
        openCopyModal,
        dispatch,
        onSelectAll,
        close,
        t
    ]);
}